
























































































































































































































































































import { Component, Vue } from "vue-property-decorator";
import lodash from "lodash";
import Password from "@/components/Password.vue";
import SelectAll, { ValueText } from "@/components/SelectAll.vue";
import * as util from "@/util";
import * as uiUtil from "@/uiUtil";
import { domain, Company, Branch, EditUser, EditPermission, User, PERMISSION_ATTRS, VueRoot, VueForm } from "@/domain";
import { emptyCompany, EditedUser, EditedPermission, ListUser } from "@/domain/types/user_manager";

@Component({
  components: { Password, SelectAll }
})
export default class UserEditDialog extends Vue {
  // New/Edit dialog
  dialog = false;
  editedItem?: EditedUser = this.defaultItem(emptyCompany());
  savingUser = false;

  // Rules
  permissionError = false;
  emailRules = [util.rules.required(this.lang.requiredEmailAlert), util.rules.email(this.lang.ruleInvalidEmail)];

  // Special permissions (SPECIAL_PERMISSION_ATTRS) unlock state
  confirmUnlockSpecial = false;
  specialPermissionsUnlocked = false;

  // Company cache
  loadedCompanies: Company[] = [];
  loadingCompany = -1;

  get lang() {
    return this.$store.state.lang.data;
  }

  get currentCompany(): Company | null {
    if (this.$store.state.selectedCompanyLoading) {
      return null;
    }
    return this.$store.state.selectedCompany;
  }

  get userCompanies(): Company[] {
    if (!this.$store.state.userCompanies) {
      return [];
    }
    return this.$store.state.userCompanies;
  }

  get currentUser(): User {
    return this.$store.state.currentUser || {};
  }

  get canUnlockSpecialPermissions(): boolean {
    return !!this.currentUser.isToken || !!this.currentUser.isGlobalAdmin;
  }

  get canEditSpecialPermissionsNow(): boolean {
    return this.canUnlockSpecialPermissions && this.specialPermissionsUnlocked;
  }

  cmdUnlockSpecialPermissions() {
    this.specialPermissionsUnlocked = true;
    this.confirmUnlockSpecial = false;
  }

  get isMobile(): boolean {
    return (this.$root as VueRoot).isMobile;
  }

  snackShow(message: string, color: string) {
    this.$emit("message", { message, color });
  }

  get currentBranches(): Branch[] {
    return uiUtil.branchComboItems(this.currentCompany, this.$store.getters.userPermission);
  }

  get isEditForm(): boolean {
    return !!this.editedItem && !this.editedItemIsNew;
  }

  async loadCompany(companyID: number) {
    this.loadingCompany = companyID;
    const company = await domain.backend.getCompanyData(companyID);
    this.loadedCompanies.push(company);
    this.loadingCompany = -1;
  }

  isCompanyLoaded(companyID: number): boolean {
    return !!this.getCompany(companyID);
  }

  getCompany(companyID: number): Company | undefined {
    if (this.currentCompany && this.currentCompany.id === companyID) {
      return this.currentCompany;
    }
    return this.loadedCompanies.find((c) => c.id === companyID);
  }

  validateEdit(): boolean {
    if (!this.editedItem) {
      return false;
    }
    let valid = true;
    if (!(this.$refs.editUserForm as VueForm).validate()) {
      valid = false;
    }
    this.permissionError = false;
    if (this.editedItem.allPermissions.length === 0 || this.editedItem.allPermissions.every((p) => p.company.id < 0)) {
      this.permissionError = true;
      valid = false;
    }
    return valid;
  }

  async cmdSave() {
    if (!this.validateEdit()) {
      return;
    }
    if (!this.editedItem) {
      return;
    }
    this.savingUser = true;
    const modifiedPermissions: EditPermission[] = this.editedItem.allPermissions.filter(this.isChanged).map((perm) => {
      const mapped: EditPermission = {
        companyID: perm.company.id,
        remove: false,
        admin: !!perm.admin,
        branchIDs: perm.branchIDs
      };
      PERMISSION_ATTRS.forEach((attr) => (mapped[attr] = !!perm[attr]));
      return mapped;
    });
    const user: EditUser = {
      edit: this.isEditForm,
      uid: "",
      permissions: modifiedPermissions
    };
    if (!this.editedItemIsNew && this.editedItem.rawUser) {
      user.uid = this.editedItem.rawUser.uid;
      user.accountChanges = {
        email: this.editedItem.email,
        displayName: this.getValue(this.editedItem.displayName, this.editedItem.rawUser.displayName),
        emailVerified: this.getValue(this.editedItem.emailVerified, this.editedItem.rawUser.emailVerified)
      };
      if (this.currentUser.isGlobalAdmin) {
        user.accountChanges.disabled = this.editedItem.disabled;
      }
    } else {
      user.accountChanges = {
        email: this.editedItem.email,
        displayName: this.getValue(this.editedItem.displayName),
        emailVerified: this.getValue(this.editedItem.emailVerified)
      };
    }
    if (this.editedItem.changePassword) {
      user.accountChanges.password = this.editedItem.newPassword;
    }
    try {
      const savedResult = await domain.backend.saveUser(user);
      util.log("Usuario guardado", savedResult);
      this.snackShow(this.lang.alertUserSavedRefresh, "success");
      if (!user.edit && (!user.accountChanges || !user.accountChanges.password) && savedResult.isNewAccount) {
        await domain.backend.resetPassword(savedResult.user.email);
        this.snackShow(this.lang.alertUserSavedSendedPasswordEmail, "success");
      }
      this.savingUser = false;
      this.$emit("save", savedResult);
      this.close();
    } catch (error) {
      util.logError("Error guardando el usuario", error);
      this.savingUser = false;
      this.snackShow(uiUtil.toErrorMsg(error, this.lang.errorCouldNotSaveUser), "error");
    }
  }

  getValue<T>(newValue: T, oldValue?: T): T | undefined {
    if (!util.isNull(newValue) && oldValue !== newValue) {
      return newValue;
    }
    return undefined;
  }

  get editedItemIsNew(): boolean {
    return !!this.editedItem && (!this.editedItem.rawUser || !this.editedItem.rawUser.uid);
  }

  editedBranchOptions(perm: EditedPermission): ValueText[] {
    let branches: { value: number; text: string }[] = [];
    const company = this.getCompany(perm.company.id);
    if (company && !util.isEmpty(company.branches)) {
      branches = (company.branches || []).map((b) => ({
        value: b.id,
        text: b.name
      }));
    }
    if (branches.length === 0 && (perm.branchIDs || []).length > 0) {
      branches = perm.branchIDs.map((b) => ({ value: b, text: `Oficina ${b}` }));
    }
    branches.sort((a, b) => a.text.localeCompare(b.text));
    return branches;
  }

  get editCompaniesAvailable(): Company[] {
    let result: Company[] = [];
    if (this.editedItem) {
      const companiesUsed = new Set(this.editedItem.allPermissions.map((p) => p.company.id));
      const adminCompanies = new Set(this.$store.getters.userPermission.adminCompanies);
      result = this.userCompanies.filter((c) => !companiesUsed.has(c.id) && adminCompanies.has(c.id));
    }
    if (result.length === 0) {
      result.push({
        id: -1,
        name: this.lang.noAvailableCompanies,
        address: [],
        branchesSummaries: []
      });
    }
    return result;
  }

  itemAddPermission(cpy: Company) {
    if (this.editedItem && cpy.id > 0) {
      const allPermissions = lodash(this.editedItem.allPermissions)
        .push(this.defaultPermission(cpy))
        .filter((p) => p.company.id > 0)
        .sortBy((p) => p.company.name)
        .value();
      this.editedItem.allPermissions = allPermissions;
      this.$nextTick(() => {
        if (this.editedItem) this.editedItem.curPermission = "C" + cpy.id;
      });
    }
  }

  isChanged(perm: EditedPermission): boolean {
    if (!perm.rawUser || !perm.rawUser.uid) {
      return true;
    }
    return (
      perm.admin !== perm.rawUser.admin ||
      PERMISSION_ATTRS.some((attr) => !perm.rawUser || perm[attr] !== perm.rawUser[attr]) ||
      !util.arrayEquals(perm.rawUser.branchIDs, perm.branchIDs)
    );
  }

  mapPermission(p: ListUser): EditedPermission {
    return {
      rawUser: p,
      company: p.company,
      admin: p.admin,
      allowTxnStatistics: p.allowTxnStatistics,
      allowConnectionGraph: p.allowConnectionGraph,
      paymentLink: p.paymentLink,
      clientAPI: p.clientAPI,
      txnModification: p.txnModification,
      txnView: util.isNull(p.txnView) ? true : p.txnView,
      vendingAdmin: p.vendingAdmin,
      topayAdmin: p.topayAdmin,
      euroshellCertificate: p.euroshellCertificate || false,
      branchIDs: p.branchIDs || []
    };
  }

  close() {
    this.dialog = false;
  }

  defaultItem(cpy: Company): EditedUser {
    return {
      rawUser: null,
      displayName: "",
      email: "",
      emailVerified: false,
      changePassword: false,
      newPassword: "",
      disabled: false,
      curPermission: "C" + cpy.id,
      allPermissions: [this.defaultPermission(cpy)]
    };
  }

  defaultPermission(cpy: Company): EditedPermission {
    return {
      rawUser: null,
      company: cpy,
      admin: false,
      allowTxnStatistics: false,
      allowConnectionGraph: false,
      paymentLink: false,
      clientAPI: false,
      txnModification: false,
      txnView: true,
      vendingAdmin: false,
      topayAdmin: false,
      euroshellCertificate: false,
      branchIDs: []
    };
  }

  async cmdResetPassword(item?: ListUser) {
    util.log({ edited: this.editedItem, item });
    let email = "";
    if (item) {
      email = item.email;
    } else if (this.editedItem) {
      email = this.editedItem.email;
    }
    if (!email) {
      this.snackShow(this.lang.alertInvalidAddress, "error");
      return;
    }
    util.log("Reset email", email);
    try {
      await domain.backend.resetPassword(email);
      this.snackShow(this.lang.resetEmailSended, "success");
    } catch (err) {
      util.logError("Error reseteando password", err);
      this.snackShow(uiUtil.toErrorMsg(err, this.lang.errorMsgEmail), "error");
    }
  }

  // ------ PUBLIC Methods ------- //
  openForNewItem() {
    const cpy = this.currentCompany || emptyCompany();
    if (cpy.id < 0) {
      cpy.name = this.lang.noPermissions;
    }
    const newItem = this.defaultItem(cpy);
    this.editedItem = newItem;
    this.resetSpecialPermissionsUnlock();
    this.dialog = true;
  }
  openForEdit(item: ListUser) {
    const permissions = lodash(item.allUsers)
      .map(this.mapPermission)
      .sortBy((p) => p.company.name)
      .value();
    this.editedItem = {
      rawUser: item,
      displayName: !item.uid ? "" : item.displayName,
      email: item.email,
      emailVerified: item.emailVerified,
      changePassword: false,
      newPassword: "",
      disabled: item.disabled,
      allPermissions: permissions,
      curPermission: "C" + item.companyID
    };
    this.resetSpecialPermissionsUnlock();
    this.dialog = true;
  }
  // ------ PUBLIC Methods ------- //

  private resetSpecialPermissionsUnlock() {
    this.confirmUnlockSpecial = false;
    this.specialPermissionsUnlocked = false;
  }
}
